import { useState } from "react";
import { CheckSquare, Plane, Plus, Sun } from "lucide-react";
import { ForumDonut } from "@/components/ForumDonut";
import { useForumColors } from "@/context/ForumColorContext";
import { alpha } from "@/lib/forumColors";
import { isAllDay, isoDate, parseISO, whenLabel } from "@/lib/eventMeta";
import { useTouchDrag } from "@/lib/useTouchDrag";

const stripe = (color) =>
  `repeating-linear-gradient(135deg, ${alpha(color, 0.35)}, ${alpha(color, 0.35)} 5px, ${alpha(
    color,
    0.08,
  )} 5px, ${alpha(color, 0.08)} 11px)`;

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const WeekBoard = ({ days, onOpenDay, onOpenEvent, onAdd, onMove, canWrite, todos = {} }) => {
  const { colorFor, forumName, slicesFor } = useForumColors();
  const today = isoDate(new Date());
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const movable = !!(canWrite && onMove);
  const touch = useTouchDrag({ enabled: movable, onDrop: onMove });

  const dragProps = (event) =>
    movable
      ? {
          draggable: true,
          onDragStart: (e) => {
            e.dataTransfer.setData("text/plain", event.event_id);
            e.dataTransfer.effectAllowed = "move";
            setDragging(event);
          },
          onDragEnd: () => {
            setDragging(null);
            setOver(null);
          },
          ...touch.handlers(event),
        }
      : {};

  const dropProps = (date) =>
    movable
      ? {
          onDragOver: (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (over !== date) setOver(date);
          },
          onDragLeave: () => setOver((d) => (d === date ? null : d)),
          onDrop: (e) => {
            e.preventDefault();
            const id = e.dataTransfer.getData("text/plain") || dragging?.event_id;
            setOver(null);
            setDragging(null);
            if (!id) return;
            const event =
              dragging?.event_id === id
                ? dragging
                : days.flatMap((d) => d.events).find((ev) => ev.event_id === id);
            if (event && event.date !== date) onMove(event, date);
          },
        }
      : {};

  const openEvent = (event) => {
    if (touch.ignoreClick()) return;
    onOpenEvent?.(event);
  };

  const lifted = touch.lift?.event;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3" data-testid="week-board">
        {days.map(({ date, cell, events }) => {
          const isToday = date === today;
          const slices = slicesFor(events);
          const dominant = slices[0]?.color;
          const fullDay = events.filter((e) => isAllDay(e));
          const timed = events.filter((e) => !isAllDay(e));
          const isTarget =
            (over === date && dragging?.date !== date) ||
            (touch.target === date && lifted && lifted.date !== date);
          return (
            <section
              key={date}
              data-testid={`week-day-${date}`}
              data-week-day={date}
              {...dropProps(date)}
              className={`surface flex flex-col overflow-hidden transition-shadow duration-200 ${
                isToday ? "ring-2 ring-primary" : ""
              } ${isTarget ? "ring-2 ring-offset-2 ring-foreground" : ""}`}
            >
              <button
                data-testid={`week-day-header-${date}`}
                onClick={() => onOpenDay?.(date)}
                className="px-3 py-3 border-b border-border text-left flex items-center gap-3 hover:bg-accent/40 transition-colors duration-200"
                style={dominant ? { background: alpha(dominant, 0.1) } : undefined}
              >
                <span className="min-w-0 flex-1">
                  <span className="label-caps block">{WEEKDAY[parseISO(date).getDay()]}</span>
                  <span className="block font-heading text-2xl font-bold leading-none mt-1">
                    {parseISO(date).getDate()}
                  </span>
                  <span className="block text-xs text-muted-foreground mt-1.5">
                    {events.length} listed
                    {cell?.clashes ? " · clash" : ""}
                  </span>
                  {todos[date]?.open ? (
                    <span
                      data-testid={`week-todo-badge-${date}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase tracking-wider"
                      style={{
                        color: todos[date].high_open ? "rgb(var(--c-fixed))" : undefined,
                      }}
                      title={`${todos[date].open} to-do(s) open`}
                    >
                      <CheckSquare className="h-3.5 w-3.5" /> {todos[date].open} to-do
                    </span>
                  ) : null}
                </span>
                <ForumDonut slices={slices} testId={`week-donut-${date}`} size={46} />
              </button>

              {fullDay.length ? (
                <div
                  className="px-3 py-2 border-b border-border space-y-1"
                  data-testid={`week-fullday-${date}`}
                >
                  {fullDay.map((e) => {
                    const color = colorFor(e);
                    const travel = e.event_type === "out_of_station";
                    return (
                      <button
                        key={e.event_id}
                        data-testid={`week-event-${e.event_id}`}
                        onClick={() => openEvent(e)}
                        {...dragProps(e)}
                        className={`w-full text-left rounded-sm px-2 py-1.5 border ${
                          dragging?.event_id === e.event_id || lifted?.event_id === e.event_id
                            ? "opacity-40"
                            : ""
                        }`}
                        style={{
                          backgroundImage: stripe(color),
                          borderColor: alpha(color, 0.55),
                          touchAction: lifted ? "none" : undefined,
                        }}
                      >
                        <span
                          className="flex items-center gap-1.5 text-[0.65rem] font-bold uppercase tracking-[0.1em]"
                          style={{ color }}
                        >
                          {travel ? (
                            <Plane className="h-3.5 w-3.5" />
                          ) : (
                            <Sun className="h-3.5 w-3.5" />
                          )}
                          {travel ? "Out of station" : "All day"}
                        </span>
                        <span className="block text-sm font-semibold leading-snug mt-0.5">
                          {e.location || e.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              <div className="p-2 space-y-2 flex-1">
                {canWrite ? (
                  <button
                    data-testid={`week-add-${date}`}
                    onClick={() => onAdd?.(date)}
                    className="w-full inline-flex items-center justify-center gap-1 border border-dashed border-border rounded-sm py-2 text-sm text-muted-foreground hover:text-foreground hover:border-foreground transition-colors duration-200"
                  >
                    <Plus className="h-4 w-4" /> Add
                  </button>
                ) : null}

                {timed.map((e) => {
                  const color = colorFor(e);
                  return (
                    <button
                      key={e.event_id}
                      data-testid={`week-event-${e.event_id}`}
                      onClick={() => openEvent(e)}
                      {...dragProps(e)}
                      title={`${forumName(e)}${movable ? " · drag or long-press to move" : ""}`}
                      className={`w-full text-left border border-l-[5px] border-border rounded-sm px-2 py-1.5 hover:brightness-105 transition-all duration-200 ${
                        dragging?.event_id === e.event_id || lifted?.event_id === e.event_id
                          ? "opacity-40"
                          : ""
                      }`}
                      style={{
                        borderLeftColor: color,
                        background: alpha(color, 0.12),
                        touchAction: lifted ? "none" : undefined,
                      }}
                    >
                      <span
                        className="block text-[0.65rem] font-bold uppercase tracking-[0.1em]"
                        style={{ color }}
                      >
                        {whenLabel(e)}
                        {e.urgent ? " · urgent" : ""}
                      </span>
                      <span className="block text-sm font-semibold leading-snug mt-0.5">
                        {e.title}
                      </span>
                      {e.clash ? (
                        <span className="mt-1 inline-block text-[0.6rem] font-bold uppercase tracking-wider text-fixed">
                          clash
                        </span>
                      ) : null}
                    </button>
                  );
                })}

                {isTarget ? (
                  <p
                    data-testid={`week-drop-hint-${date}`}
                    className="border border-dashed border-foreground rounded-sm py-2 text-center text-xs font-semibold"
                  >
                    Move here
                  </p>
                ) : null}
              </div>
            </section>
          );
        })}
      </div>

      {movable ? (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="week-board-hint">
          Drag a listing onto another day to move it — on a touch screen, press and hold it first.
        </p>
      ) : null}

      {touch.lift ? (
        <div
          data-testid="touch-drag-ghost"
          className="fixed z-50 pointer-events-none px-3 py-2 rounded-sm border shadow-xl bg-card max-w-[14rem]"
          style={{
            left: touch.lift.x - 90,
            top: touch.lift.y - 54,
            borderColor: colorFor(touch.lift.event),
            background: alpha(colorFor(touch.lift.event), 0.94),
          }}
        >
          <span className="block text-[0.65rem] font-bold uppercase tracking-[0.1em] text-white/90">
            {touch.target ? `Move to ${touch.target}` : "Hold and drag"}
          </span>
          <span className="block text-sm font-semibold leading-snug text-white truncate">
            {touch.lift.event.title}
          </span>
        </div>
      ) : null}
    </>
  );
};

